import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  Stack,
  Alert,
  Chip,
  Avatar,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { createLeadApi } from "../../../api/leads.api";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    height: 48,
    borderRadius: "12px",
  },
};

const ProfileSidebar = ({ lead, onClose, onUpdateContactName }) => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    source: "WhatsApp",
  });

  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  // Check if lead already exists with a name
  const existingClientName = lead?.client_name?.trim();
  const isExistingLead = Boolean(existingClientName);

  /* ===== Prefill from chat ===== */
  useEffect(() => {
    if (lead) {
      setForm({
        name: existingClientName || "",
        phone: lead?.phone || lead?.whatsapp?.phone || lead?.contact || "",
        email: lead?.email || "",
        source: "WhatsApp",
      });
    }
  }, [lead, existingClientName]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  /* ===== Submit Lead ===== */
  const handleSubmit = async () => {
    if (!form.name) return;

    try {
      setLoading(true);

      await createLeadApi({
        client_name: form.name,
        contact: form.phone,
        email: form.email || undefined,
        custom_fields: { lead_source: form.source },
      });

      // ✅ Update contact name in parent state (sidebar, header, profile)
      const contactId = lead?._id || lead?.id;
      if (onUpdateContactName && contactId) {
        onUpdateContactName(contactId, form.name);
      }

      // ✅ show snackbar FIRST
      setSnackbarOpen(true);

      // ✅ close sidebar AFTER snackbar
      setTimeout(() => {
        onClose();
      }, 3000); // match snackbar duration
    } catch (error) {
      console.error("Create lead failed", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box
        width={380}
        height="80vh"
        display="flex"
        flexDirection="column"
        bgcolor="#fff"
      >
        {/* ===== Header ===== */}
        <Box
          px={2}
          py={1.5}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography fontWeight={600}>Profile Info</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        {/* ===== Content ===== */}
        <Box flex={1} p={2} overflow="auto">
          <Stack spacing={2}>
            {isExistingLead ? (
              <Alert
                severity="info"
                sx={{
                  bgcolor: "#E3F2FD",
                  color: "#1565C0",
                  borderRadius: 2,
                  minHeight: 44,
                }}
              >
                This contact is already a lead
              </Alert>
            ) : (
              <Alert
                severity="warning"
                sx={{
                  visibility: form.name ? "hidden" : "visible",
                  bgcolor: "#FFF4E5",
                  color: "#663C00",
                  borderRadius: 2,
                  minHeight: 44,
                }}
              >
                Client name is required
              </Alert>
            )}

            {/* Lead Preview */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar />
              <Box flex={1}>
                <Typography fontWeight={600}>
                  {form.name || "Unknown Lead"}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {form.phone || "No phone number"}
                </Typography>
              </Box>

              <Chip
                label="WhatsApp"
                size="small"
                icon={<WhatsAppIcon />}
                sx={{
                  bgcolor: "#E9F7EF",
                  color: "#1E7F4F",
                  fontWeight: 500,
                }}
              />
            </Stack>

            <Typography fontWeight={600}>
              {isExistingLead ? "Lead Details" : "Add Lead"}
            </Typography>

            <TextField
              placeholder="Client Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              disabled={isExistingLead}
              fullWidth
              size="small"
              sx={inputSx}
            />

            <TextField
              value={form.phone || "No phone number"}
              disabled
              fullWidth
              size="small"
              sx={inputSx}
            />

            <TextField
              placeholder="Email (optional)"
              name="email"
              value={form.email}
              onChange={handleChange}
              fullWidth
              size="small"
              sx={inputSx}
            />

            <TextField
              value="WhatsApp"
              disabled
              fullWidth
              size="small"
              sx={inputSx}
            />
          </Stack>
        </Box>

        {/* ===== Footer ===== */}
        <Box p={2} display="flex" gap={1} borderTop="1px solid #eee">
          <Button variant="outlined" fullWidth onClick={onClose}>
            CANCEL
          </Button>

          <Button
            variant="contained"
            fullWidth
            disabled={!form.name || isExistingLead}
            onClick={() => setOpenDialog(true)}
            sx={{
              height: 48,
              borderRadius: "24px",
              bgcolor: "#F44336",
              "&:hover": { bgcolor: "#D32F2F" },
              "&.Mui-disabled": {
                bgcolor: isExistingLead ? "#E0E0E0" : undefined,
              },
            }}
          >
            {isExistingLead ? "ALREADY A LEAD" : "ADD NEW LEAD"}
          </Button>
        </Box>
      </Box>

      {/* ===== Confirm Popup ===== */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add New Lead</DialogTitle>

        <DialogContent>
          <Typography>Are you sure you want to add this lead?</Typography>

          <Box mt={2}>
            <Typography fontWeight={600}>{form.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {form.phone}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={() => setOpenDialog(false)}>
            CANCEL
          </Button>

          <Button
            variant="contained"
            onClick={() => {
              setOpenDialog(false);
              handleSubmit();
            }}
            disabled={loading}
            sx={{
              bgcolor: "#F44336",
              "&:hover": { bgcolor: "#D32F2F" },
            }}
          >
            {loading ? "ADDING..." : "CONFIRM ADD"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Success Snackbar ===== */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert severity="success" variant="filled">
          Lead added successfully 🎉
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProfileSidebar;
